import React, { createContext, useEffect, useState } from 'react';

import { Device, Insight, Loadable, MotionData, Value } from '../types';
import { LoadableRecord } from '../types/LoadableRecord';
import { metricsService } from '../api/services/metricsService';

interface MetricsContextType {
  device?: Device;
  metrics: LoadableRecord<Array<Value>>;
  activity: LoadableRecord<Array<Value>>;
  insights: Loadable<Insight>;
  vitals: Loadable<Value>;
  metricsInterval: number | undefined;
  activityInterval: number | undefined;
  insightsInterval: number | undefined;
  vitalsInterval: number | undefined;
  motionData?: MotionData | undefined;
  setMetricsInterval: (interval: number | undefined) => void;
  setActivityInterval: (interval: number) => void;
  setInsightsInterval: (interval: number | undefined) => void;
  setVitalsInterval: (interval: number | undefined) => void;
}

const loadableMetrics: LoadableRecord<Array<Value>> = { loading: true };
const loadableActivity: LoadableRecord<Array<Value>> = { loading: true };
const loadableInsights: Loadable<Insight> = { loading: true };
const loadableVitals: Loadable<Value> = { loading: true };

const initialContext: MetricsContextType = {
  metrics: loadableMetrics,
  activity: loadableActivity,
  insights: loadableInsights,
  vitals: loadableVitals,
  metricsInterval: undefined,
  activityInterval: undefined,
  insightsInterval: undefined,
  vitalsInterval: undefined,
  setMetricsInterval: () => {},
  setActivityInterval: () => {},
  setInsightsInterval: () => {},
  setVitalsInterval: () => {},
};

const MetricsContext = createContext<MetricsContextType>(initialContext);

interface MetricsProviderProps {
  device?: Device;
  children: React.ReactNode;
}

function MetricsProvider({ device, children }: MetricsProviderProps) {
  const [metrics, setMetrics] = useState<LoadableRecord<Array<Value>>>({ loading: true });
  const [metricsInterval, setMetricsInterval] = useState<number | undefined>();
  const [activity, setActivity] = useState<LoadableRecord<Array<Value>>>({ loading: true });
  const [activityInterval, setActivityInterval] = useState<number | undefined>();
  const [insights, setInsights] = useState<Loadable<Insight>>({ loading: true });
  const [insightsInterval, setInsightsInterval] = useState<number | undefined>();
  const [vitals, setVitals] = useState<Loadable<Value>>({ loading: true });
  const [vitalsInterval, setVitalsInterval] = useState<number | undefined>();
  const [motionData, setMotionData] = useState<MotionData | undefined>();

  useEffect(() => {
    if (!device) return;

    // Load metrics
    setMetrics({ loading: true });
    const from = metricsInterval ? new Date(Date.now() - metricsInterval) : undefined;
    metricsService
      .getMetrics(device.id, from)
      .then((response) => setMetrics({ loading: false, data: response.data }))
      .catch((err) => setMetrics({ loading: false, error: err }));
  }, [device, metricsInterval]);

  useEffect(() => {
    if (!device) return;

    // Load activity
    setActivity({ loading: true });
    const from = activityInterval ? new Date(Date.now() - activityInterval) : undefined;
    metricsService
      .getActivity(device.id, from)
      .then((response) => setActivity({ loading: false, data: response.data }))
      .catch((err) => setActivity({ loading: false, error: err }));

    // Load motion data
    setMotionData(undefined);
    metricsService
      .getMotionData(device.id, from)
      .then((response) => setMotionData(response.data))
      .catch(() => setMotionData(undefined));
  }, [device, activityInterval]);

  useEffect(() => {
    if (!device) return;

    // Load insights
    setInsights({ loading: true });
    const from = insightsInterval ? new Date(Date.now() - insightsInterval) : undefined;
    metricsService
      .getInsights(device.id, from)
      .then((response) => setInsights({ loading: false, data: response.data }))
      .catch((err) => setInsights({ loading: false, error: err }));
  }, [device, insightsInterval]);

  useEffect(() => {
    if (!device) return;

    // Load vitals
    setVitals({ loading: true });
    const from = vitalsInterval ? new Date(Date.now() - vitalsInterval) : undefined;
    metricsService
      .getVitals(device.id, from)
      .then((response) => setVitals({ loading: false, data: [response.data] }))
      .catch((err) => setVitals({ loading: false, error: err }));
  }, [device, vitalsInterval]);

  return (
    <MetricsContext.Provider
      value={{
        device,
        metrics,
        metricsInterval,
        activity,
        activityInterval,
        insights,
        insightsInterval,
        vitals,
        vitalsInterval,
        motionData,
        setMetricsInterval,
        setActivityInterval,
        setInsightsInterval,
        setVitalsInterval,
      }}
    >
      {children}
    </MetricsContext.Provider>
  );
}

export { MetricsContext, MetricsProvider };
